import type {
    ClientRequest,
    ClientResponse,
    Store,
    StoreDraft,
    StoreUpdate,
    StoreUpdateAction,
} from '@commercetools/platform-sdk';
import { createSyncStores } from '@commercetools/sync-actions';

import type { ApiClientDetails, QueryArgs } from '../../types';
import { getQueryPredicate, QueryKey } from '../utils';

export function storeApi({ apiRoot, ctpClient }: ApiClientDetails) {
    function storeClientRequest(args?: QueryArgs): ClientRequest {
        return apiRoot().stores().get(args).clientRequest();
    }

    function fetchStoresByQuery(args: QueryArgs): Promise<Store[]> {
        const clientRequest = apiRoot().stores().get(args).clientRequest();

        return ctpClient().process(clientRequest, payload => Promise.resolve(payload.body.results), {
            accumulate: true,
        });
    }

    async function fetchStoresWith(expand?: string | string[]): Promise<Store[]> {
        const storeReq = storeClientRequest({ queryArgs: { expand } });
        return await ctpClient().process(storeReq, payload => Promise.resolve(payload.body.results), {
            accumulate: true,
        });
    }

    async function getStoresWhere(
        where?: string,
        expand?: string | string[],
        pageSize?: number,
        offset = 0
    ): Promise<Store[]> {
        const storeReq = storeClientRequest({
            queryArgs: { where, expand, offset },
        });

        const total = pageSize && { total: pageSize };

        return ctpClient().process(storeReq, payload => Promise.resolve(payload.body.results), {
            accumulate: true,
            ...total,
        });
    }

    async function createStore(store: StoreDraft): Promise<ClientResponse<Store>> {
        return apiRoot()
            .stores()
            .post({
                body: store,
            })
            .execute();
    }

    async function updateStore({ id, version }: Store, actions: StoreUpdateAction[]): Promise<ClientResponse<Store>> {
        const body: StoreUpdate = { version, actions };

        return apiRoot().stores().withId({ ID: id }).post({ body }).execute();
    }

    function syncStore(previous: Store, next: StoreDraft): Promise<ClientResponse<Store>> {
        const syncStores = createSyncStores([], {
            shouldOmitEmptyString: true,
        });

        const actions: StoreUpdateAction[] = syncStores.buildActions(next, previous);

        return updateStore(previous, actions);
    }

    async function saveStore(store: StoreDraft): Promise<Store> {
        const [existingStore] = await getStoresWhere(`key="${store.key}"`);

        if (!existingStore) {
            return (await createStore(store)).body;
        }

        return (
            await syncStore(existingStore, {
                ...store,
                distributionChannels: store.distributionChannels && [
                    ...store.distributionChannels,
                    ...existingStore.distributionChannels,
                ],
            })
        ).body;
    }

    async function getStoresByIds(ids: string[]) {
        return fetchStoresByQuery({
            queryArgs: { where: getQueryPredicate(ids, QueryKey.ID) },
        });
    }

    async function getStoreById(id: string): Promise<Store> {
        return (await apiRoot().stores().withId({ ID: id }).get().execute()).body;
    }

    async function getStoreByKey(key: string, expand?: string | string[]): Promise<Store> {
        return (await apiRoot().stores().withKey({ key }).get({ queryArgs: { expand } }).execute()).body;
    }

    return {
        fetchStoresByQuery,
        fetchStoresWith,
        getStoresWhere,
        saveStore,
        getStoresByIds,
        getStoreById,
        createStore,
        updateStore,
        getStoreByKey,
    };
}
